import { motion } from "motion/react";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import WhatsAppFloating from "../../../components/feature/WhatsAppFloating";
import CookieBanner from "../../../components/feature/CookieBanner";
import QuoteFormSection from "../../home/components/QuoteFormSection";
import SEO from "@/components/feature/SEO";

const services = [
  {
    title: "Reparar motor existente",
    desc: "Diagnóstico y reparación de motores que no responden, hacen ruido o se paran a mitad de camino.",
    price: "60 - 120 €",
    icon: "ri-tools-line",
  },
  {
    title: "Cambiar motor estropeado",
    desc: "Sustitución completa del motor por uno nuevo de calidad. Incluye instalación y prueba.",
    price: "150 - 250 €",
    icon: "ri-exchange-line",
  },
  {
    title: "Motorizar persiana manual",
    desc: "Convertimos tu persiana manual en eléctrica. Mando a distancia incluido. Garantía 2 años.",
    price: "200 - 350 €",
    icon: "ri-remote-control-line",
  },
];

const signs = [
  "Hace ruidos extraños",
  "Tarda mucho en subir o bajar",
  "Se para a mitad de recorrido",
  "No responde al mando a distancia",
  "Tiene más de 10 años",
];

const brands = [
  { name: "Somfy", desc: "Líder mundial en motores para persianas" },
  { name: "Cherubini", desc: "Robustos y duraderos, ideales para uso intensivo" },
  { name: "Becker", desc: "Precisión alemana y larga vida útil" },
  { name: "Nice", desc: "Calidad italiana con excelente relación calidad-precio" },
  { name: "Came", desc: "Motores potentes para persianas grandes" },
];

const faqs = [
  {
    q: "¿Qué motor es mejor para mi persiana?",
    a: "Depende del uso. Para persianas estándar Somfy y Cherubini son excelentes. Si buscas precio Becker y Nice son muy buenos. Te asesoramos gratis.",
  },
  {
    q: "¿Cuánto dura un motor de persiana?",
    a: "Un motor de calidad dura entre 10 y 15 años con uso normal. Si tu persiana se usa mucho, puede durar algo menos. Por eso damos garantía de 2 años.",
  },
  {
    q: "¿Se puede motorizar cualquier persiana?",
    a: "Casi todas las persianas manuales se pueden motorizar. Solo necesitamos ver el tamaño del cajón para confirmarlo. La visita es gratuita.",
  },
  {
    q: "¿Necesito instalación eléctrica especial?",
    a: "No necesariamente. La mayoría de motores actuales funcionan con conexión estándar de 220V. En algunos casos recomendamos un electricista, pero lo valoramos en la visita.",
  },
  {
    q: "¿Cuál es el precio de un motor Somfy?",
    a: "Un motor Somfy con instalación suele estar entre 150 y 250 € según el modelo. La motorización completa de una persiana manual ronda los 250-350 €.",
  },
];

export default function MotorPersianas() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Motor Persiana Málaga | Cambio y Reparación con Garantía"
        description="Cambiamos y reparamos motores de persianas en Málaga. Somfy, Cherubini, Becker. Garantía 2 años. ☎ 951 12 07 70"
        canonical="/persianas/motor-persianas/"
        ogType="website"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Persianas", path: "/persianas" }, { name: "Motor de persianas" }]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-[60%] flex flex-col gap-6">
                <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill w-fit">
                  <span>⚡</span> 24h · Respuesta en 20 min
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-text-primary">
                  Motor de Persiana en Málaga: Reparación, Cambio y Motorización
                </h1>
                <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl">
                  Cambiamos, reparamos o instalamos motores nuevos. Marcas: Somfy, Cherubini, Becker. Garantía 2 años.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg md:text-xl px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                  >
                    <span>📞</span> 951 12 07 70
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Pedir presupuesto
                  </a>
                </div>
              </div>
              <div className="w-full lg:w-[40%] relative">
                <div className="relative rounded-3xl overflow-hidden lg:rotate-1 shadow-xl">
                  <div className="absolute inset-0 bg-blue-900/35 z-10 pointer-events-none"></div>
                  <img
                    src="https://images.unsplash.com/photo-1631084650668-5e1f72bf6ce5?w=1200&q=80&auto=format&fit=crop"
                    alt="Motor de persiana en Málaga - reparación y cambio"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICIOS DE MOTOR */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Servicios de motor</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {services.map((s, i) => (
                <motion.div
                  key={s.title}
                  className="bg-white rounded-[20px] p-8 border-t-[6px] border-secondary shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-secondary/10 text-secondary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${s.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{s.title}</h3>
                  <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">{s.desc}</p>
                  <div className="inline-block font-heading font-bold text-xl text-secondary">{s.price}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ¿CUÁNDO CAMBIAR EL MOTOR? */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="w-full lg:w-1/2">
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Cuándo cambiar el motor?</h2>
                <p className="font-body text-lg text-text-secondary mb-6">
                  Estas son las señales de que tu motor de persiana necesita revisión o sustitución.
                </p>
                <div className="flex flex-col gap-3">
                  {signs.map((s, i) => (
                    <motion.div
                      key={s}
                      className="flex items-center gap-3 bg-surface-alt rounded-xl px-5 py-4"
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                    >
                      <span className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full shrink-0">
                        <i className="ri-error-warning-line"></i>
                      </span>
                      <span className="font-body text-base text-text-primary">{s}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2 bg-text-primary rounded-3xl p-8 md:p-10">
                <h3 className="font-heading font-bold text-2xl text-white mb-6">Marcas con las que trabajamos</h3>
                <div className="flex flex-col gap-4">
                  {brands.map((b, i) => (
                    <motion.div
                      key={b.name}
                      className="bg-slate-800 rounded-xl p-5 border border-slate-700"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                    >
                      <div className="font-heading font-bold text-lg text-white mb-1">{b.name}</div>
                      <div className="font-body text-sm text-slate-300">{b.desc}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Preguntas frecuentes sobre motores</h2>
              <div className="flex flex-col gap-4">
                {faqs.map((f, i) => (
                  <motion.div
                    key={i}
                    className="bg-white rounded-2xl p-6 shadow-sm"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2" itemProp="name">{f.q}</h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="font-body text-sm text-text-secondary leading-relaxed" itemProp="text">{f.a}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}